import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { STROKE_EFFECT } from '../constants/tailwind';

type MsgFrom = 'system' | 'user' | 'ai';
interface Msg { from: MsgFrom; text: string; }

const STACK_CHIPS = ['React', 'FastAPI', 'Postgres', 'S3', 'Pinecone', 'OpenAI'];

const DEMO_FLOW: Msg[] = [
  { from: 'user', text: 'What is the main argument in section 3?' },
  { from: 'ai',   text: 'Section 3 argues that retrieval quality, not model size, sets the ceiling on answer accuracy — the notes cite two ablations where a smaller model with better chunking wins.' },
  { from: 'user', text: 'Summarise the grading policy.' },
  { from: 'ai',   text: 'Grading is 40% project, 30% midterm, 30% final. Late work loses 10% per day, with one free 48-hour extension across the term.' },
];

export default function Landing() {
  const navigate = useNavigate();
  const [showDemo, setShowDemo]       = useState(false);
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'uploading' | 'done'>('idle');
  const [progress, setProgress]       = useState(0);
  const [messages, setMessages]       = useState<Msg[]>([]);
  const [input, setInput]             = useState('');
  const [aiTyping, setAiTyping]       = useState(false);
  const [autoPlaying, setAutoPlaying] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const timers    = useRef<ReturnType<typeof setTimeout>[]>([]);
  const tick      = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, aiTyping]);

  function clearAll() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (tick.current) { clearInterval(tick.current); tick.current = null; }
  }

  function later(ms: number, fn: () => void) {
    timers.current.push(setTimeout(fn, ms));
  }

  function closeDemo() {
    clearAll();
    setShowDemo(false);
    setUploadPhase('idle');
    setProgress(0);
    setMessages([]);
    setAiTyping(false);
    setAutoPlaying(false);
    setInput('');
  }

  function startUpload() {
    setUploadPhase('uploading');
    let p = 0;
    tick.current = setInterval(() => {
      p = Math.min(100, p + 4 + Math.random() * 6);
      setProgress(p);
      if (p >= 100) {
        clearInterval(tick.current!); tick.current = null;
        later(420, afterUpload);
      }
    }, 70);
  }

  function afterUpload() {
    setUploadPhase('done');
    const sys: Msg = { from: 'system', text: '📄 lecture_notes.pdf — 38 chunks indexed into Pinecone' };
    setMessages([sys]);
    setAutoPlaying(true);
    let t = 700;
    DEMO_FLOW.forEach(msg => {
      const isAi = msg.from === 'ai';
      if (isAi) { later(t, () => setAiTyping(true)); t += 1700; }
      later(t, () => {
        setAiTyping(false);
        setMessages(m => [...m, msg]);
        if (msg === DEMO_FLOW[DEMO_FLOW.length - 1]) setAutoPlaying(false);
      });
      t += isAi ? 900 : 550;
    });
  }

  const canChat = uploadPhase === 'done' && !autoPlaying;

  function send() {
    const text = input.trim();
    if (!text || !canChat) return;
    setInput('');
    setMessages(m => [...m, { from: 'user', text }]);
  }

  const uploadStage =
    progress < 45 ? 'Uploading to S3…' :
    progress < 85 ? 'Chunking + embedding…' : 'Writing vectors…';

  return (
    <div className="h-screen bg-cover bg-center bg-[url('/src/assets/bg.png')] flex flex-col overflow-hidden text-white">

      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 bg-black/20 backdrop-blur-sm border-b border-white/10 shrink-0">
        <span className="font-bold text-lg tracking-tight">QuickNote</span>
        <div className="flex items-center gap-5">
          <a href="https://github.com/anthu2708/ragNote" target="_blank" rel="noreferrer" className="text-sm text-white/50 hover:text-white transition">GitHub</a>
          <button onClick={() => navigate('/login')} className="text-sm text-white/60 hover:text-white transition">Sign in</button>
          <div className={STROKE_EFFECT}>
            <button onClick={() => navigate('/register')} className="px-5 py-1.5 text-sm font-semibold rounded-full bg-gradient-chat-response hover:opacity-90 transition">
              Register
            </button>
          </div>
        </div>
      </nav>

      {/* Body */}
      <div className="flex-1 flex items-center px-8 py-6 gap-10 max-w-6xl mx-auto w-full">

        {/* Left — hero */}
        <div className="flex-1 space-y-5">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 border border-white/20 rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            End-to-end RAG project
          </span>

          <h1 className="text-5xl font-extrabold leading-tight drop-shadow-xl">
            Upload files once.<br />
            <span className="text-purple-300">Ask questions</span>{' '}anytime.
          </h1>

          <p className="text-gray-300 text-sm leading-relaxed max-w-sm">
            Upload documents, embed into Pinecone, query your knowledge base in plain language.
          </p>

          <div className="flex flex-wrap gap-1.5 pt-1">
            {STACK_CHIPS.map(s => (
              <span key={s} className="text-[11px] px-2.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-white/55">{s}</span>
            ))}
          </div>

          <div className="flex gap-3 pt-2 flex-wrap">
            <div className={STROKE_EFFECT}>
              <button onClick={() => navigate('/register')} className="font-semibold px-7 py-2.5 bg-gradient-chat-response rounded-full text-white text-sm hover:opacity-90 transition">
                Get started →
              </button>
            </div>
            <button onClick={() => navigate('/login')} className="font-semibold px-7 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition">
              Sign in
            </button>
            <button
              onClick={() => showDemo ? closeDemo() : setShowDemo(true)}
              className="font-semibold px-7 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition"
            >
              {showDemo ? 'Hide demo' : '▶ Demo'}
            </button>
          </div>
        </div>

        {/* Right — demo panel */}
        <div
          className={`shrink-0 self-center bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl flex flex-col overflow-hidden transition-all duration-500 ease-out ${
            showDemo ? 'w-[400px] opacity-100 translate-x-0' : 'w-0 opacity-0 translate-x-8 pointer-events-none'
          }`}
          style={{ height: 420 }}
        >
          {/* Chrome */}
          <div className="flex items-center gap-2.5 px-5 py-3 border-b border-white/10 bg-white/5 shrink-0">
            <span className="text-xs text-white/35 flex-1 font-mono">QuickNote demo</span>
            {uploadPhase === 'uploading' && <span className="text-[11px] text-white/40">uploading…</span>}
            {uploadPhase === 'done' && <span className="text-[11px] text-green-400/80">✓ indexed</span>}
          </div>

          {/* Progress bar */}
          {uploadPhase === 'uploading' && (
            <div className="px-5 py-2 bg-white/5 border-b border-white/10 shrink-0">
              <div className="flex justify-between text-[10px] text-white/40 mb-1">
                <span className="font-mono">lecture_notes.pdf</span>
                <span>{Math.round(Math.min(progress, 100))}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-chat-response rounded-full transition-all duration-100" style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
              <div className="text-[10px] text-white/30 mt-1">{uploadStage}</div>
            </div>
          )}

          {/* Chat body */}
          <div className="flex-1 px-4 py-4 overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {uploadPhase === 'idle' && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-2xl">📄</div>
                <p className="text-sm text-white/50">Upload a document to start asking questions</p>
                <button onClick={startUpload} className="text-xs px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/60 hover:bg-white/20 transition">
                  Try the demo →
                </button>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.from === 'system' ? (
                  <span className="text-[11px] text-white/35 bg-white/5 border border-white/10 rounded-full px-3 py-1 mx-auto font-mono">{m.text}</span>
                ) : m.from === 'user' ? (
                  <span className="bg-white/15 border border-white/20 text-white/90 text-xs rounded-2xl rounded-tr-sm px-3.5 py-2 max-w-[78%]">{m.text}</span>
                ) : (
                  <span className="bg-gradient-chat-response text-white/90 text-xs rounded-2xl rounded-tl-sm px-3.5 py-2 max-w-[82%] leading-relaxed">{m.text}</span>
                )}
              </div>
            ))}

            {aiTyping && (
              <div className="flex justify-start">
                <span className="bg-gradient-chat-response rounded-2xl rounded-tl-sm px-4 py-2.5 flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce [animation-delay:0ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce [animation-delay:300ms]" />
                </span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2 shrink-0">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              disabled={!canChat}
              placeholder={uploadPhase !== 'done' ? 'Upload a file first…' : autoPlaying ? 'Demo playing…' : 'Ask about lecture_notes.pdf…'}
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs text-white placeholder-white/25 outline-none focus:border-white/30 disabled:opacity-40 transition"
            />
            <button onClick={send} disabled={!canChat || !input.trim()} className="w-7 h-7 rounded-full bg-gradient-chat-response flex items-center justify-center text-xs disabled:opacity-30 hover:opacity-90 transition">↑</button>
          </div>
        </div>
      </div>
    </div>
  );
}
